import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { SchoolsApi, SetOwnerPayload, SchoolOwner, SetOwnerResult } from '@/services/superadmin.api';
import { Modal } from '@/components/ui/Modal';
import { Field, Input } from '@/components/ui/Input';

interface OwnerModalTarget {
  id: string;
  name: string;
  code?: string | null;
}

/**
 * View/create/reset a school's tenant admin (owner). Shared between
 * SchoolsPage's per-row action, the Platform "Admin Users" screen, and the
 * Org Console's own org-scoped "Admin Users" screen — see docs/DEVIATIONS.md.
 * `getOwner`/`setOwner` default to the Platform (superadmin-only) endpoints;
 * the Org Console passes its own org-scoped equivalents instead.
 */
export function OwnerModal({
  school,
  onClose,
  onSaved,
  getOwner = SchoolsApi.getOwner,
  setOwner = SchoolsApi.setOwner,
}: {
  school: OwnerModalTarget;
  onClose: () => void;
  onSaved: (msg: string) => void;
  getOwner?: (schoolId: string) => Promise<SchoolOwner>;
  setOwner?: (schoolId: string, payload: SetOwnerPayload) => Promise<SetOwnerResult>;
}) {
  const { data: owner, isLoading } = useQuery({
    queryKey: ['school-owner', school.id],
    queryFn: () => getOwner(school.id),
  });

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const hydrated = useRef(false);
  useEffect(() => {
    if (owner && !hydrated.current) {
      setName(owner.name ?? '');
      setEmail(owner.email ?? '');
      hydrated.current = true;
    }
  }, [owner]);

  const exists = !!owner;
  const save = useMutation({
    mutationFn: () =>
      setOwner(school.id, {
        name: name.trim() || undefined,
        email: email.trim() || undefined,
        password: password || undefined,
      }),
    onSuccess: (r) =>
      onSaved(
        `${r.created ? 'Created' : 'Updated'} admin for ${school.name} — ${r.email}${password ? ' · password reset' : ''}.`,
      ),
  });

  const valid = exists
    ? Boolean(name.trim() || email.trim() || password)
    : Boolean(name.trim() && email.trim() && password.length >= 8);

  return (
    <Modal
      open
      onClose={onClose}
      title={`School admin — ${school.name ?? ''}`}
      description={`Login code: ${school.code ?? '—'}`}
      size="md"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-1.5"
            onClick={() => save.mutate()}
            disabled={save.isPending || !valid}
          >
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {exists ? 'Save changes' : 'Create admin'}
          </button>
        </>
      }
    >
      {isLoading ? (
        <div className="py-6 text-center text-slate-400">Loading…</div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-slate-500">
            {exists
              ? 'Change the admin’s name/email, or set a new password. Leave the password blank to keep the current one.'
              : 'No admin exists for this school yet. Create one — they log in with the School Code above + this email and password.'}
          </p>
          <Field label="Admin name">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Principal Name"
            />
          </Field>
          <Field label="Login email">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="owner@school.edu"
            />
          </Field>
          <Field
            label={exists ? 'New password (optional)' : 'Password'}
            hint="At least 8 characters"
          >
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={exists ? 'Leave blank to keep current' : 'Min 8 chars'}
            />
          </Field>
          {save.error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {errMsg(save.error)}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}
